import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import Topbar from '../../../container/PulseSurvey/Topbar';
import DownloadReportButton from '../../B2BDashboard/DownloadReportButton';
import DashboardFilterModal, {
  EMPTY_FILTERS,
  countActiveFilters
} from '../../B2BDashboard/DashboardFilterModal';
import { REPORT_SCOPE } from '../../../api/companyReportApi';
import { StandardButton } from '../StandardUI';
import filterIcon from '../../../assets/images/filter.svg';

/**
 * Filter state for a super-admin section, so the pages inside a layout can
 * read what the admin picked without prop-drilling through the route wrapper.
 *
 * The company panel's equivalent is useCompanySectionFilter in
 * component/B2B/CompanySectionLayout.jsx. Two contexts rather than one shared
 * one because the two layouts own different state — the company version is
 * always scoped to one company, this one carries an audience as well.
 */
const SectionFilterContext = createContext({
  department: '',
  accountType: '',
  filters: EMPTY_FILTERS
});

export const useSectionFilter = () => useContext(SectionFilterContext);

/**
 * Section shell: page topbar, title block and the underlined tab row from the
 * Figma guideline (the same treatment as
 * B2B/CompanyAdmin/PeoplePlan/components/PeoplePlanTabs.jsx).
 *
 * Access guards stay in the feature's own layout so this can be reused by
 * company-admin sections with different rules. The only state it owns is the
 * filter set, and only when a section opts in with `filterable`.
 *
 * Topbar lives under container/PulseSurvey for historical reasons; it is the
 * panel-wide top bar, and every page renders its own because the global one in
 * routes/index.js is commented out.
 */

const tabShape = PropTypes.shape({
  label: PropTypes.string.isRequired,
  // Routed mode: the path this tab navigates to.
  path: PropTypes.string,
  // Controlled mode: the identity this tab reports through onChange.
  value: PropTypes.string,
  // Extra prefixes that should also light this tab up (sibling routes).
  matchPrefixes: PropTypes.arrayOf(PropTypes.string)
});

/** A tab's identity, whichever mode it is in. */
const tabId = (tab) => tab.value ?? tab.path;

/**
 * Longest-prefix matching keeps the right tab highlighted on nested pages
 * (a company detail under /sleep/b2b keeps "B2B Logs" active).
 */
export const useActiveTab = (tabs = []) => {
  const location = useLocation();

  return tabs.reduce((best, tab) => {
    if (!tab.path) return best;
    const prefixes = tab.matchPrefixes || [tab.path];
    const matched = prefixes.reduce((longest, prefix) => {
      const hit = location.pathname === prefix || location.pathname.startsWith(`${prefix}/`);
      if (!hit) return longest;
      return prefix.length > longest ? prefix.length : longest;
    }, 0);
    if (!matched) return best;
    return !best || matched > best.matched ? { ...tab, matched } : best;
  }, null);
};

/**
 * The underlined tab row, in one of two modes.
 *
 * ROUTED (default): each tab carries a `path` and clicking navigates; the
 * active tab is derived from the URL. This is what a section built out of
 * routes wants, and it keeps a tab linkable and survivable across a refresh.
 *
 * CONTROLLED: pass `value` and `onChange` and the row becomes a plain
 * segmented control over local state. Some screens genuinely are not routed —
 * the super admin's View Company panes are one component switching on
 * `useState` — and without this mode those screens either stay on their old
 * pill buttons or get their tab row hand-rolled a second time.
 */
export const SectionTabs = ({ tabs, className = '', value, onChange }) => {
  const navigate = useNavigate();
  const routedTab = useActiveTab(tabs);

  const controlled = typeof onChange === 'function';
  const activeId = controlled ? value : routedTab?.path;

  return (
    <div className={`border-b border-shoorah-borderGray dark:border-shoorah-darkBorder ${className}`}>
      <div className="flex gap-8 overflow-x-auto">
        {tabs.map((tab) => {
          const id = tabId(tab);
          return (
            <button
              key={id}
              type="button"
              onClick={() => (controlled ? onChange(id) : navigate(tab.path))}
              className={`pb-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                activeId === id
                  ? 'border-shoorah-primary text-shoorah-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

SectionTabs.propTypes = {
  tabs: PropTypes.arrayOf(tabShape).isRequired,
  className: PropTypes.string,
  // Controlled mode. Supplying onChange switches the row out of routed mode.
  value: PropTypes.string,
  onChange: PropTypes.func
};

/**
 * `reportFeatureKey` opts a section into its own PDF download. One prop rather
 * than each hub wiring a button into `action` itself, so all ~22 of them get
 * the same control in the same place, and a hub that also has an action keeps
 * it — the two sit side by side rather than one replacing the other.
 *
 * The key must match the feature's key in utils/dashboardFeatures.js and the
 * backend MANIFEST, or the download 404s.
 */
const SectionLayout = ({
  title,
  description,
  tabs = [],
  action,
  reportFeatureKey = null,
  reportFilters = null,
  reportApi = null,
  reportSubject = null,
  filterable = false,
  loadFilterData = null,
  showHeading = true,
  documentTitle,
  children,
  className = ''
}) => {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filterData, setFilterData] = useState({
    countries: [],
    departments: [],
    ethnicities: [],
    loaded: false
  });

  /**
   * The option lists only populate the dialog, so they are fetched the first
   * time it opens rather than on mount — one fewer request in the way of the
   * section rendering. Same trade the company layout makes.
   */
  useEffect(() => {
    if (!filterable || !loadFilterData || !isFilterOpen || filterData.loaded) return;

    let cancelled = false;
    Promise.resolve(loadFilterData())
      .then((data) => {
        if (cancelled) return;
        // DashboardFilterModal maps over each list without guarding, so every
        // key must always be an array even when the request half-failed.
        setFilterData({
          countries: Array.isArray(data?.countries) ? data.countries : [],
          departments: Array.isArray(data?.departments) ? data.departments : [],
          ethnicities: Array.isArray(data?.ethnicities) ? data.ethnicities : [],
          loaded: true
        });
      })
      .catch(() => {
        // The section still works unfiltered; a toast here would interrupt
        // reading a dashboard to report a dropdown that is merely empty.
        if (!cancelled) setFilterData((current) => ({ ...current, loaded: true }));
      });

    return () => {
      cancelled = true;
    };
  }, [filterable, loadFilterData, isFilterOpen, filterData.loaded]);

  const activeFilterCount = countActiveFilters(filters);

  const filterValue = useMemo(
    () => ({
      // Lower-cased because the hub endpoints match on a normalised value —
      // the same contract the company layout exposes.
      department: (filters.department || '').toLowerCase(),
      filters
    }),
    [filters]
  );

  return (
    <div className={`relative lg:mb-[45px] ${className}`}>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{documentTitle || `${title} | Shoorah Admin`}</title>
      </Helmet>

      <Topbar title={title} infoBtn={false} />

      <div className="mt-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className={`min-w-0 ${showHeading ? '' : 'hidden'}`}>
          <h2 className="text-xl font-medium text-shoorah-btn dark:text-white">{title}</h2>
          {description && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{description}</p>
          )}
        </div>
        {action || reportFeatureKey || filterable ? (
          <div className="shrink-0 flex items-center gap-2">
            {action}
            {filterable ? (
              <StandardButton variant="secondary" onClick={() => setIsFilterOpen(true)}>
                <img src={filterIcon} alt="" className="w-4 h-4 dark:invert" />
                Filter
                {activeFilterCount > 0 && (
                  <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-semibold bg-shoorah-btn text-white dark:bg-shoorah-btnDark dark:text-shoorah-btn">
                    {activeFilterCount}
                  </span>
                )}
              </StandardButton>
            ) : null}
            {reportFeatureKey ? (
              <DownloadReportButton
                scope={REPORT_SCOPE.FEATURE}
                featureKey={reportFeatureKey}
                featureLabel={title}
                filters={reportFilters || {}}
                api={reportApi}
                subject={reportSubject}
                variant="button"
                label="Download"
              />
            ) : null}
          </div>
        ) : null}
      </div>

      {tabs.length > 0 ? <SectionTabs tabs={tabs} className="mt-5 mb-6" /> : null}

      <div>
        <SectionFilterContext.Provider value={filterValue}>
          {children}
        </SectionFilterContext.Provider>
      </div>

      {filterable ? (
        <DashboardFilterModal
          open={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          filters={filters}
          onApply={setFilters}
          filterData={filterData}
        />
      ) : null}
    </div>
  );
};

SectionLayout.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.node,
  tabs: PropTypes.arrayOf(tabShape),
  action: PropTypes.node,
  reportFeatureKey: PropTypes.string,
  reportFilters: PropTypes.object,
  // Which report data layer the download button talks to, and who the report
  // is about. Both omitted means the company panel's own reports.
  reportApi: PropTypes.object,
  reportSubject: PropTypes.object,
  // Opt in to the filter button, the dialog and the filter context. Off by
  // default so the sections that predate it are unchanged.
  filterable: PropTypes.bool,
  // List pages put the heading inside the TableCard, as the Figma list
  // screens do, so they turn this one off rather than showing it twice.
  showHeading: PropTypes.bool,
  // Async supplier of the dialog's option lists. Called once, lazily, the
  // first time the dialog opens.
  loadFilterData: PropTypes.func,
  documentTitle: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string
};

export default SectionLayout;
